/**
 * EntityRecordScope
 *
 * Scope-opener for entity-record consumers (routed entity page, modals,
 * sidebars, embedded entities). Mounts the keyed store for `(entityId,
 * recordId)` (refcount + grace-period managed by `useEntityRecordStore`)
 * and publishes an `entity`-sourced `PageScope` for its subtree, so deep
 * leaves resolve their scope positionally — several openers plus the
 * ambient form scope are live at one commit.
 *
 * Optional `entityGroupId` and `locked` props sync into the store instance
 * once the scope is mounted. Optional `onSelectRecord` rides the same proxy
 * and is read at event time only (e.g. `<Entity />` creating a record and
 * asking the opener to switch to it).
 */
import { FC, ReactNode, useEffect } from 'react';
import { PageScopeContext, usePageScopeProxy } from '../pageScope';
import { useEntityRecordStore } from './useEntityRecordStore';
import { entityRecordActions } from './entityRecordActions';

export type EntityRecordScopeProps = {
    entityId: string;
    recordId?: string;
    entityGroupId?: string;
    locked?: boolean;
    onSelectRecord?: (recordId: string) => void;
    children: ReactNode;
};

export const EntityRecordScope: FC<EntityRecordScopeProps> = ({
    entityId,
    recordId,
    entityGroupId,
    locked,
    onSelectRecord,
    children,
}) => {
    useEntityRecordStore(entityId, recordId);

    useEffect(() => {
        if (entityGroupId !== undefined) {
            entityRecordActions.selectEntityGroup(entityId, recordId, entityGroupId);
        }
    }, [entityGroupId, entityId, recordId]);

    useEffect(() => {
        if (locked !== undefined) {
            entityRecordActions.setEntityLocked(entityId, recordId, locked);
        }
    }, [locked, entityId, recordId]);

    const scope = usePageScopeProxy({
        source: 'entity' as const,
        entityId,
        recordId,
        onSelectRecord,
    });

    return <PageScopeContext.Provider value={scope}>{children}</PageScopeContext.Provider>;
};
